<template>
  <article
    v-if="showHeader || mainElementMainImage || showSlider"
    :key="keyid"
    :data-key="keyid"
    :data-banner-type="product.banner.type"
    :class="bemBlockMod(bannerClass)"
  >
    <BaseTitleSubtitle
      v-if="showHeader"
      :class="bemElement('title-subtitle')"
      :title="product.banner.mainElement.title"
      :subtitle="product.banner.mainElement.subtitle"
      :color="product.banner.mainElement.color"
    />

    <BaseLink
      v-if="mainElementMainImage"
      v-bind="product.banner.mainElement.link"
      :class="bemElement('link')"
    >
      <BaseImage
        :class="bemElement('pic')"
        :data-url="product.banner.mainElement.link.url"
        :image-classes="bemElement('image')"
        :image="getDefaultImageFromProductImageSet(mainElementMainImage)"
        :loading="forceMainImageToLoad ? 'eager' : null"
        :placeholder="getPlaceholderImageFromProductImageSet(mainElementMainImage)"
        :sizes="getSizes()"
        :srcset="getSrcsetFromProductImageSet(mainElementMainImage)"
        :use-title="false"
        @image-click="mainElementClick()"
      />
    </BaseLink>

    <BaseCarousel
      v-if="showSlider"
      :class="bemElement('slider')"
      :are-controls-shown="false"
      :items="product.banner.productSlider"
      :swiper-options="{
        allowTouchMove: true,
        loop: false,
        slidesPerView: 2.5,
        spaceBetween: 2,
      }"
    >
      <template #default="slotProps">
        <BaseProductTile
          :are-carousel-controls-shown="false"
          :context="context"
          :force-main-image-to-load="true"
          :grid-index="slotProps.index"
          :has-carousel="false"
          :hide-prices="hidePrices"
          :hide-product-names="hideProductNames"
          :hide-tags="hideTags"
          :keyid="`${slotProps.item.id}-${slotProps.index}`"
          :minimal="minimal"
          :product="slotProps.item"
          :show-swatches="false"
          :type="type"
          :url="slotProps.item.selectedProductUrl || ''"
        />
      </template>
    </BaseCarousel>
  </article>
</template>

<script>
import { paramCase } from 'change-case';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import BaseLink from './BaseLink.vue';
import BaseProductTile from './BaseProductTile.vue';
import BaseTitleSubtitle from './BaseTitleSubtitle.vue';
import getAnalyticsEventCenter from '../../common/getAnalyticsEventCenter';
import getFrontConfig from '../../common/getFrontConfig';
import windowResizeMixin from '../mixins/windowResizeMixin';

const analyticsEventCenter = getAnalyticsEventCenter();

const {
  productImages: { imageSets },
  responsive: { bp_768: bp768 },
  vue: {
    BaseProductTile: {
      context: { plpSliderBanner },
    },
    TheProductList: {
      bannerTypes: { plpSimpleImage },
    },
  },
} = getFrontConfig();

const SIZES = {
  PLP_GRID: '(min-width: 768px) 25vw, 50vw',
  PLP_DOUBLE_WIDTH: '100vw',
};

export default {
  name: 'TheGridBanner',

  components: {
    BaseCarousel,
    BaseImage,
    BaseLink,
    BaseProductTile,
    BaseTitleSubtitle,
  },

  mixins: [
    bemClassesMixin,
    productPropBaseMixin,
    productImageSetMixin,
    windowResizeMixin,
  ],

  props: {
    context: {
      type: String,
      required: false,
      default: plpSliderBanner,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    gridIndex: {
      type: Number,
      required: true,
    },
    hidePrices: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideProductNames: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideTags: {
      type: Boolean,
      default: false,
      required: false,
    },
    keyid: {
      type: String,
      default: '',
      required: false,
    },
    minimal: {
      type: Boolean,
      default: false,
      required: false,
    },
    type: {
      type: String,
      validator: (prop) => [
        'productList',
        'search',
      ].includes(prop),
      default: 'productList',
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp768,
  }),

  computed: {
    bannerClass() {
      const listClass = {
        minimal: this.minimal,
      };
      const type = paramCase(this.product.banner.type);
      listClass[type] = true;
      return listClass;
    },
    mainElementImageSet() {
      let imageSet = imageSets.PLP;
      if (this.context === plpSliderBanner) {
        imageSet = imageSets.PLAIN;
      }
      const bylImages = this.isBreakpointDesktop && this.product.banner.mainElement.bylImagesDesktop
        ? this.product.banner.mainElement.bylImagesDesktop
        : this.product.banner.mainElement.bylImages;
      return this.getProductImageSets(imageSet, bylImages);
    },
    mainElementMainImage() {
      return this.mainElementImageSet?.[0];
    },
    showHeader() {
      return (this.product.banner.mainElement.title || this.product.banner.mainElement.subtitle)
        && !this.minimal;
    },
    showSlider() {
      return this.product.banner.productSlider && !this.minimal;
    },
  },

  methods: {
    mainElementClick() {
      if (this.product.banner.mainElement.link.url) {
        // Send info for Analytics
        let bannerType;
        const payloads = analyticsEventCenter.EVENTS.PAYLOADS.productListBannerClicked;
        if (this.product.banner.type === 'imageFull') {
          bannerType = payloads.type.bannerImageFull;
        } else if (this.product.banner.type === 'imageDouble') {
          bannerType = payloads.type.bannerImageDouble;
        } else if (this.product.banner.type === 'imageSimple') {
          bannerType = payloads.type.bannerImageSimple;
        } else {
          // Carousel main image
          bannerType = payloads.type.bannerCarousel;
        }
        // Send info for Analytics
        analyticsEventCenter.publish(
          analyticsEventCenter.EVENTS.productListBannerClicked,
          {
            url: this.product.banner.mainElement.link.url,
            type: bannerType,
          },
        );
      }
    },
    getSizes() {
      return this.product.banner.type === plpSimpleImage
        ? SIZES.PLP_GRID
        : SIZES.PLP_DOUBLE_WIDTH;
    },
  },
};
</script>
